import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import { ChevronDown, ChevronRight, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DESIGNATIONS = [20, 30, 40, 50, 60, 70, 80];

const DESIGNATION_COLORS = {
  20: 'bg-emerald-500', 30: 'bg-green-500', 40: 'bg-lime-500',
  50: 'bg-orange-500', 60: 'bg-orange-500', 70: 'bg-red-400', 80: 'bg-red-600',
};

const DESIGNATION_BG_LIGHT = {
  20: 'bg-emerald-100 text-emerald-800', 30: 'bg-green-100 text-green-800',
  40: 'bg-lime-100 text-lime-800', 50: 'bg-orange-100 text-orange-800',
  60: 'bg-orange-100 text-orange-800', 70: 'bg-red-100 text-red-800',
  80: 'bg-red-200 text-red-900',
};

const DESIGNATION_BG_DARK = {
  20: 'bg-emerald-900/40 text-emerald-300', 30: 'bg-green-900/40 text-green-300',
  40: 'bg-lime-900/40 text-lime-300', 50: 'bg-orange-900/40 text-orange-300',
  60: 'bg-orange-900/40 text-orange-300', 70: 'bg-red-900/40 text-red-300',
  80: 'bg-red-900/50 text-red-200',
};

const BEDROOM_LABELS = { 0: 'Studio', 1: '1 BR', 2: '2 BR', 3: '3 BR', 4: '4 BR', 5: '5 BR' };

export default function AitMonthlyGrid({ buildingId }) {
  const { theme } = useTheme();
  const { fetchMonthlyDesignations, updateMonthlyDesignations, carryForwardDesignations } = useAit();
  const isDark = theme === 'dark';

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { unitId, month }
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [carryingForward, setCarryingForward] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [buildingId, year]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEditingCell(null);
      }
    }
    if (editingCell) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingCell]);

  const loadData = async () => {
    setLoading(true);
    const result = await fetchMonthlyDesignations(buildingId, year);
    if (result) setData(result);
    setLoading(false);
  };

  // Group units by bedroom count
  const groupedUnits = useMemo(() => {
    if (!data?.units) return {};
    const groups = {};
    data.units.forEach(unit => {
      const key = unit.bedroom_count ?? 0;
      if (!groups[key]) groups[key] = [];
      groups[key].push(unit);
    });
    return groups;
  }, [data]);

  const bedroomKeys = Object.keys(groupedUnits).sort((a, b) => Number(a) - Number(b));

  // Check if January is empty for all units (show carry-forward button)
  const showCarryForward = useMemo(() => {
    if (!data?.units?.length) return false;
    return data.units.every(u => {
      const jan = u.months?.[1] ?? u.months?.['1'];
      return !jan;
    });
  }, [data]);

  const handleCellClick = (unitId, month) => {
    setEditingCell({ unitId, month });
  };

  const handleDesignationChange = async (unitId, month, newDesignation) => {
    setEditingCell(null);
    const result = await updateMonthlyDesignations(buildingId, [
      { unit_id: unitId, year, month, designation: newDesignation }
    ]);
    if (result.success) {
      // Update local data optimistically
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          units: prev.units.map(u =>
            u.unit_id === unitId
              ? { ...u, months: { ...u.months, [month]: newDesignation } }
              : u
          )
        };
      });
    } else {
      toast.error(result.error || 'Failed to update');
    }
  };

  const handleCarryForward = async () => {
    setCarryingForward(true);
    const result = await carryForwardDesignations(buildingId, year);
    if (result.success) {
      toast.success(`Carried forward ${result.carried} unit(s) from ${year - 1}`);
      await loadData();
    } else {
      toast.error(result.error || 'Failed to carry forward');
    }
    setCarryingForward(false);
  };

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-blue-500" size={24} />
      </div>
    );
  }

  if (!data || !data.units?.length) {
    return (
      <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        No units in this building. Add units first to track monthly designations.
      </div>
    );
  }

  return (
    <div>
      {/* Year selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Compliance Year
        </h3>
        <div className="flex items-center gap-2">
          {showCarryForward && (
            <button
              onClick={handleCarryForward}
              disabled={carryingForward}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                isDark
                  ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10'
                  : 'border-blue-200 text-blue-600 hover:bg-blue-50'
              } disabled:opacity-50`}
            >
              {carryingForward ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
              Carry Forward from {year - 1}
            </button>
          )}
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className={`text-sm rounded-lg border px-3 py-1.5 ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bedroom groups */}
      <div className="space-y-4">
        {bedroomKeys.map(brKey => {
          const brUnits = groupedUnits[brKey];
          const stats = data.bedroom_stats?.[brKey] || {};
          const isCollapsed = collapsedGroups[brKey];
          const label = BEDROOM_LABELS[brKey] || `${brKey} BR`;

          return (
            <div key={brKey} className={`rounded-xl border overflow-hidden ${
              isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'
            }`}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(brKey)}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {label}
                  </span>
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    ({stats.total || brUnits.length} units)
                  </span>
                </div>
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="whitespace-nowrap text-green-500">{stats.qualified || 0} qualified</span>
                  <span className="whitespace-nowrap">{stats.non_qualified || 0} non-qualified</span>
                  <span className="whitespace-nowrap font-medium">{stats.percentage_qualified || 0}%</span>
                </div>
              </button>

              {/* Monthly grid table */}
              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className={`w-full text-xs ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    <thead>
                      <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 sticky left-0 z-10 bg-inherit min-w-[70px]">Unit</th>
                        <th className="px-2 py-2 text-center font-medium text-gray-500 dark:text-gray-400 min-w-[36px]" title="Initial Qualified Group">IQG</th>
                        {MONTHS.map((m, i) => (
                          <th key={i} className="px-1 py-2 text-center font-medium text-gray-500 dark:text-gray-400 min-w-[44px]">{m}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700/50' : 'divide-gray-100'}`}>
                      {brUnits.map(unit => (
                        <tr key={unit.unit_id} className={`${isDark ? 'hover:bg-gray-800/80' : 'hover:bg-gray-50/50'} transition-colors`}>
                          <td className={`px-3 py-1.5 font-medium sticky left-0 z-10 ${isDark ? 'bg-gray-800/90' : 'bg-white'}`}>
                            {unit.unit_number}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {unit.initial_qualified ? (
                              <CheckCircle2 size={12} className="text-green-500 inline" />
                            ) : (
                              <span className="opacity-30">—</span>
                            )}
                          </td>
                          {MONTHS.map((_, monthIdx) => {
                            const month = monthIdx + 1;
                            const designation = unit.months?.[month] ?? unit.months?.[String(month)];
                            const isEditing = editingCell?.unitId === unit.unit_id && editingCell?.month === month;
                            const colorClass = designation
                              ? (isDark ? DESIGNATION_BG_DARK[designation] : DESIGNATION_BG_LIGHT[designation])
                              : '';

                            return (
                              <td key={month} className="px-0.5 py-1 text-center relative">
                                <button
                                  onClick={() => handleCellClick(unit.unit_id, month)}
                                  className={`w-full py-1 rounded text-xs font-medium transition-colors ${
                                    designation
                                      ? `${colorClass}`
                                      : isDark
                                        ? 'text-gray-600 hover:bg-gray-700/50'
                                        : 'text-gray-300 hover:bg-gray-100'
                                  }`}
                                >
                                  {designation ? `${designation}` : '·'}
                                </button>

                                {/* Inline dropdown */}
                                {isEditing && (
                                  <div
                                    ref={dropdownRef}
                                    className={`absolute z-50 mt-1 left-1/2 -translate-x-1/2 rounded-lg shadow-lg border p-1.5 min-w-[52px] ${
                                      isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                                    }`}
                                  >
                                    {DESIGNATIONS.map(d => (
                                      <button
                                        key={d}
                                        onClick={() => handleDesignationChange(unit.unit_id, month, d)}
                                        className={`block w-full text-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                                          designation === d
                                            ? `text-white ${DESIGNATION_COLORS[d]}`
                                            : isDark
                                              ? 'hover:bg-gray-700 text-gray-300'
                                              : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                      >
                                        {d}%
                                      </button>
                                    ))}
                                    <button
                                      onClick={() => handleDesignationChange(unit.unit_id, month, null)}
                                      className={`block w-full text-center px-2 py-1 rounded text-xs transition-colors mt-0.5 border-t ${
                                        isDark
                                          ? 'hover:bg-gray-700 text-gray-500 border-gray-700'
                                          : 'hover:bg-gray-100 text-gray-400 border-gray-100'
                                      }`}
                                    >
                                      Clear
                                    </button>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
